import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Button, Descriptions, Divider, Image } from "antd";
import { PhoneOutlined, CarOutlined, CheckCircleOutlined, SwapOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../../../constants";
import { getAgentColor, transmissionTypeLabel, engineLayoutLabel, drivetrainLabel, useQv4Theme } from "../../helpers";
import { StaffAvatar } from "../../../staff/StaffAvatar";
import { CustomerProfileButton } from "../shared/CustomerProfileButton";
import { normalizeTaskImages } from "../../models/shared/taskDetailModel";
import { taskAge } from "../../models/workflow-drawer/taskAge";
import { vehicleDisplay } from '../../models/shared/customerVehiclesV1Model';

import { TaskActivityTimeline } from "./TaskActivityTimeline";

dayjs.extend(utc);

const fmtDate = (val) => val ? dayjs(val).format("DD MMM YYYY, hh:mm A") : "—";

export function OverviewTab({ task, afterSales, parts = [], userMap, movements = [], onReassign, communicationControls, onShowParts, onOpenCustomerProfile, taskActions = [] }) {
  const { t } = useQv4Theme();
  const assignee = task.assigned_user;
  const assigneeName = assignee
    ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
    : null;
  const images = normalizeTaskImages(task.images);
  const timeline = Array.isArray(task.timeline) ? task.timeline : [];
  const partNames = parts.map(part => part.part_name || `Part #${part.id}`).join(", ");
  const vehicleName = vehicleDisplay(task.vehicle);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  const age = taskAge(dayjs(task.created_at).valueOf(), now);
  const truncatedNameStyle = { flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {(assigneeName || onReassign || taskActions.length > 0) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
          <StaffAvatar size={32} userId={assignee?.id ?? task.assigned_to} name={assigneeName || "Unassigned"} pfp={assignee?.pfp} style={{ backgroundColor: getAgentColor(assigneeName || "Unassigned"), fontSize: 13 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", flexWrap: "nowrap" }}>
              <span style={{ fontWeight: 600, fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{assigneeName || "Unassigned"}</span>
              {onReassign && <Button size="small" style={{ flexShrink: 0 }} aria-label="Reassign" icon={<SwapOutlined />} onClick={onReassign}>Reassign</Button>}
              {taskActions.length > 0 && <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>{taskActions}</div>}
            </div>
            <div style={{ fontSize: 11, color: t.textSub }}>{assignee?.user_type || "Staff"}</div>
          </div>
        </div>
      )}

      <Descriptions column={1} size="small" bordered
        styles={{ label: { width: 130, fontWeight: 600, fontSize: 12 }, content: { fontSize: 12, maxWidth: 0 } }}>
        {(task.customer?.customer_name || task.customer?.customer_mobile) && (
          <Descriptions.Item label="Customer">
            <span style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", minWidth: 0 }}>
              {task.customer.customer_name && <span title={task.customer.customer_name} style={truncatedNameStyle}>{task.customer.customer_name}</span>}
              {task.customer?.customer_mobile && <>
                <span style={{ flexShrink: 0, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {task.customer.customer_name && <span>·</span>}
                  <PhoneOutlined style={{ fontSize: 11 }} />
                  {task.customer.customer_mobile}
                </span>
                <span style={{ flexShrink: 0, marginLeft: "auto" }}><CustomerProfileButton phone={task.customer.customer_mobile} onBeforeOpen={onOpenCustomerProfile} /></span>
              </>}
            </span>
          </Descriptions.Item>
        )}
        {task.customer?.customer_real_name && task.customer.customer_real_name !== task.customer.customer_name && (
          <Descriptions.Item label="Real Name">{task.customer.customer_real_name}</Descriptions.Item>
        )}
        {task.vehicle && (
          <Descriptions.Item label="Vehicle">
            <span style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", minWidth: 0 }}>
              <CarOutlined style={{ fontSize: 11, flexShrink: 0 }} />
              <span title={vehicleName} style={truncatedNameStyle}>{vehicleName}</span>
              {task.vehicle.vin && <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>{vehicleName && "· "}{task.vehicle.vin}</span>}
            </span>
          </Descriptions.Item>
        )}
        {task.vehicle?.transmission_type && (
          <Descriptions.Item label="Transmission">{transmissionTypeLabel(task.vehicle.transmission_type)}</Descriptions.Item>
        )}
        {task.vehicle?.transmission_code && (
          <Descriptions.Item label="Transmission Code">{task.vehicle.transmission_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_code && (
          <Descriptions.Item label="Engine Code">{task.vehicle.engine_code}</Descriptions.Item>
        )}
        {task.vehicle?.engine_displacement && (
          <Descriptions.Item label="Engine Displacement">{task.vehicle.engine_displacement}</Descriptions.Item>
        )}
        {task.vehicle?.engine_layout && (
          <Descriptions.Item label="Engine Layout">{engineLayoutLabel(task.vehicle.engine_layout)}</Descriptions.Item>
        )}
        {task.vehicle?.drivetrain && (
          <Descriptions.Item label="Drivetrain">{drivetrainLabel(task.vehicle.drivetrain)}</Descriptions.Item>
        )}
        {task.vehicle?.fuel_type && (
          <Descriptions.Item label="Fuel Type">{task.vehicle.fuel_type}</Descriptions.Item>
        )}
        {task.vehicle?.country_spec && (
          <Descriptions.Item label="Country Spec">{task.vehicle.country_spec}</Descriptions.Item>
        )}
        {task.vehicle?.trim_info && (
          <Descriptions.Item label="Trim Info">{task.vehicle.trim_info}</Descriptions.Item>
        )}
        {task.vehicle?.notes && (
          <Descriptions.Item label="Vehicle Notes">{task.vehicle.notes}</Descriptions.Item>
        )}
        <Descriptions.Item label="Parts">
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", minWidth: 0 }}>
            <span title={partNames} style={{ flex: "1 1 0", minWidth: 0, width: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {partNames || "No parts added"}
            </span>
            {onShowParts && <Button size="small" onClick={onShowParts} style={{ flexShrink: 0 }}>More info</Button>}
          </div>
        </Descriptions.Item>
        {task.created_at && (
          <Descriptions.Item label="Created">{fmtDate(task.created_at)}{age && <span style={{ color: t.textSub }}> ({age})</span>}</Descriptions.Item>
        )}
      </Descriptions>

      {communicationControls}

      {images.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: t.textSub }}>
            Images ({images.length})
          </div>
          <Image.PreviewGroup>
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {images.map((src, i) => {
                const url = src.startsWith("http") ? src : `${API_DOMAIN}${src}`;
                return (
                  <Image key={i} src={url} width={80} height={80}
                    style={{ objectFit: "cover", borderRadius: 6, flexShrink: 0, cursor: "pointer", border: `1px solid ${t.border}` }}
                    preview={{ src: url }}
                  />
                );
              })}
            </div>
          </Image.PreviewGroup>
        </div>
      )}

      <TaskActivityTimeline timeline={timeline} movements={movements} parts={parts} userMap={userMap} taskStatus={task.status} />

      {task.remarks && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: t.textSub }}>Remarks</div>
          <div style={{ fontSize: 12, color: t.textSub, whiteSpace: "pre-wrap", background: t.headerBg, padding: "8px 10px", borderRadius: 6, border: `1px solid ${t.border}` }}>
            {task.remarks}
          </div>
        </div>
      )}

      {afterSales && (
        <div>
          <Divider style={{ margin: "4px 0 12px" }} />
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <CheckCircleOutlined style={{ color: "#52c41a" }} /> Sale Confirmed
          </div>
          <Descriptions column={2} size="small" bordered
            styles={{ label: { fontWeight: 600, fontSize: 12 }, content: { fontSize: 12 } }}>
            {afterSales.so_number && (
              <Descriptions.Item label="SO #" span={afterSales.po_number ? 1 : 2}>
                {afterSales.so_url
                  ? <a href={afterSales.so_url} target="_blank" rel="noreferrer">{afterSales.so_number}</a>
                  : afterSales.so_number}
              </Descriptions.Item>
            )}
            {afterSales.po_number && (
              <Descriptions.Item label="PO #">
                {afterSales.po_url
                  ? <a href={afterSales.po_url} target="_blank" rel="noreferrer">{afterSales.po_number}</a>
                  : afterSales.po_number}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      )}
    </div>
  );
}
